import React from 'react';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import * as ReactRedux from 'react-redux';
import { bindActionCreators } from 'redux';
import withInit from 'WithInit';
import Spinner from 'Spinner';
import StaticBuilderFormattedFields from 'StaticBuilder/StaticBuilderFormattedFields';
import StaticBuilderSection from 'StaticBuilder/StaticBuilderSection';
import NavSideBarList from 'StaticBuilder/NavSideBarList/NavSideBarListWithSections';
import SelfServeBuilderEditor from 'SelfServeBuilder/SelfServeBuilderEditor';
import NavSideBarMenuButtonWithRedux from 'StaticBuilder/NavSideBarMenuButton';
import ButtonGroup from 'ExportToolbar/ButtonGroup';
import PrintButton from 'ExportToolbar/PrintButton';
import { RecipientBuilderIntroModal, RecipientBuilderFinishModal } from 'SelfServeModals';
import ModalTypeEnum from 'Modals/ModalConstants';
import { setIsScalingEnabled, setPagesFromFile } from 'SelfServeBuilder/SelfServeBuilderActions';
import DocumentShareConstants from 'DocumentShare/DocumentShareConstants';
import { StaticBuilderProgressBar } from 'StaticBuilder';
import SignatureAuditButton from 'SignatureAuditButton';
import { createEventForDocumentShare } from 'FormSwiftAPI/Event/EventActions';
import SelfServeRecipientBuilderFinishedSidebar from './RecipientFinishedSideBar';
import {
  associateDocumentShare,
  finishAndSend,
  getSectionDataForShare,
  highlightField,
  loadNextSelfServeRecipientBuilderSection,
  loadPreviousSelfServeRecipientBuilderSection,
  loadSelfServeRecipientBuilderSection,
  saveAndContinue,
  saveSelfServeRecipientBuilderSectionContent,
} from './SelfServeRecipientBuilderActions';
import SelfServeBuilderEditorWithTooltip from './SelfServeBuilderEditorWithTooltip';
import { DisableOverlay } from './components';

const serialize = require('dom-form-serializer').serialize;

const SELF_SERVE_FIELD_TYPE_TO_STATIC_FIELD_TYPE = {
  SELF_SERVE_BUILDER_TEXTBOX: 'text_field',
  SELF_SERVE_BUILDER_CHECKBOX: 'select_multiple',
  SELF_SERVE_BUILDER_MULTIPLE_CHOICE: 'radio',
  SELF_SERVE_BUILDER_SIGNATURE: 'signature',
};

export class SelfServeRecipientBuilder extends React.Component {
  static trackAuditButtonClick() {
    window._track('View Template Responses Signature History', 'Template Builder');
  }

  constructor(props) {
    super(props);

    this.getFormattedField = this.getFormattedField.bind(this);
    this.getFormattedSectionsData = this.getFormattedSectionsData.bind(this);
    this.getSectionKeys = this.getSectionKeys.bind(this);
    this.getFormattedFields = this.getFormattedFields.bind(this);
    this.getHeader = this.getHeader.bind(this);
    this.saveAndContinue = this.saveAndContinue.bind(this);
    this.loadSection = this.loadSection.bind(this);
    this.saveAndSign = this.saveAndSign.bind(this);
    this.renderEditorPreview = this.renderEditorPreview.bind(this);
    this.renderExportButtons = this.renderExportButtons.bind(this);
    this.scrollToSignature = this.scrollToSignature.bind(this);
    this.loadPreviousStaticBuilderSection = this.loadPreviousStaticBuilderSection.bind(this);
    this.finish = this.finish.bind(this);
    this.isSignatureType = this.isSignatureType.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleFinish = this.handleFinish.bind(this);
    this.highlightSignature = this.highlightSignature.bind(this);

    this.state = {
      activeModal: '',
    };
  }

  componentDidMount() {
    if (!this.props.isRecipientPreviewModal) {
      this.props.getSectionDataForShare(this.props.shareKey);
    }
    this.props.setIsScalingEnabled();

    if (!window.COOKIE.get('hasSeenSelfServeRecipientIntroModal')) {
      window.COOKIE.set('hasSeenSelfServeRecipientIntroModal', 1);
      this.openModal(ModalTypeEnum.SELF_SERVE_RECIPIENT_BUILDER_INTRO);
    }
  }

  getFormattedSectionsData() {
    const { fieldOutline, documentName } = this.props;
    if (!fieldOutline || !fieldOutline.sections.length) return {};
    const sectionsArray = fieldOutline.sections;

    // Restructuring section data to play nice with exsisting NavSideBarList component.
    const formattedSectionsData = {
      [documentName]: [],
    };

    for (let i = 0; i < sectionsArray.length; i += 1) {
      formattedSectionsData[documentName].push(sectionsArray[i].name);
    }
    return formattedSectionsData;
  }

  getFormattedField(fieldId) {
    const field = this.props.fieldOutline.fields[fieldId];
    const fieldGroup = this.props.fieldOutline.fieldGroups[fieldId];
    const invalidFieldId = !(field || fieldGroup);
    const invalidField = field && !SELF_SERVE_FIELD_TYPE_TO_STATIC_FIELD_TYPE[field.type];
    const invalidFieldGroup = fieldGroup &&
      !SELF_SERVE_FIELD_TYPE_TO_STATIC_FIELD_TYPE[fieldGroup.type];

    if (invalidFieldId || invalidField || invalidFieldGroup) {
      return null;
    }

    if (field) {
      return {
        // `isMultiple` is a flag to determine whether we can add more
        // groups of fields through a button titled `Add Another`
        // This has nothing to do with the fact that this is not a grouped field.
        isMultiple: false,
        field: {
          id: fieldId,
          label: field.label,
          type: SELF_SERVE_FIELD_TYPE_TO_STATIC_FIELD_TYPE[field.type],
          initialValue: this.props.fieldData[fieldId] || '',
        },
      };
    }

    // fieldId points to fieldGroup
    const self = this;
    const options = [];
    const selectedValues = {};

    fieldGroup.fieldIds.forEach((fieldGroupItemId) => {
      options.push({
        id: fieldGroupItemId,
        text: self.props.fieldOutline.fields[fieldGroupItemId].label,
        value: fieldGroupItemId,
      });

      selectedValues[fieldGroupItemId] = (
        self.props.fieldData[fieldId] || []
      ).indexOf(fieldGroupItemId) >= 0;
    });


    return {
      // `isMultiple` is a flag to determine whether we can add more
      // groups of fields through a button titled `Add Another`
      // This has nothing to do with the fact that this is a grouped field.
      isMultiple: false,
      field: {
        id: fieldId,
        isSelfServe: true,
        label: fieldGroup.label,
        type: SELF_SERVE_FIELD_TYPE_TO_STATIC_FIELD_TYPE[fieldGroup.type],
        options,
        initialValue: this.props.fieldData[fieldId] || '',
        initialValues: selectedValues,
      },
    };
  }

  getSectionKeys() {
    const { fieldOutline } = this.props;
    if (!fieldOutline || !fieldOutline.sections.length) return [];
    const sections = fieldOutline.sections;
    return sections.map(section => (
      section.id
    ));
  }

  getFormattedFields() {
    const { fieldOutline, step } = this.props;
    if (!fieldOutline || !fieldOutline.sections.length) return [];
    const formattedFields = [];
    const { sections } = fieldOutline;
    const activeSection = sections[step];

    const self = this;
    activeSection.fieldIds.forEach((fieldId) => {
      const formattedField = self.getFormattedField(fieldId);
      if (formattedField) {
        formattedFields.push(formattedField);
      }
    });
    return formattedFields;
  }

  getHeader() {
    const { fieldOutline, step } = this.props;
    if (!fieldOutline || !fieldOutline.sections.length) return '';
    const { sections } = fieldOutline;
    const activeSection = sections[step];
    return activeSection.name;
  }

  openModal(activeModal) {
    this.setState({
      activeModal,
    });
  }

  handleFinish() {
    if (this.props.isPreviewMode) {
      window._track('Close Preview | Preview Modal', 'Template Builder');
      this.finish();
    } else {
      window._track('Finish And Send | Recipient Builder', 'Recipient Builder');
      this.openModal(ModalTypeEnum.SELF_SERVE_RECIPIENT_BUILDER_FINISH);
    }
  }

  closeModal() {
    this.setState({
      activeModal: '',
    });
  }

  async finish() {
    await this.saveAndContinue();
    await this.props.finishAndSend(this.props.shareKey);
    if (window.User.isLoggedIn && !this.props.isPreviewMode) {
      this.props.associateDocumentShare(this.props.shareKey);
    }
  }

  loadSection(sectionNum) {
    const payload = serialize(document.getElementById('staticBuilderForm'));
    if (!this.props.isPreviewMode) {
      this.props.saveSelfServeRecipientBuilderSectionContent(payload, this.props.shareKey);
    }
    this.props.loadSelfServeRecipientBuilderSection(sectionNum);
    this.scrollToSignature(sectionNum);
  }

  isSignatureType(fieldId) {
    const field = this.props.fieldOutline.fields[fieldId];
    const isGroup = this.props.fieldOutline.fieldGroups[fieldId];
    return !isGroup && field.type === 'SELF_SERVE_BUILDER_SIGNATURE';
  }

  scrollToSignature(sectionNum) {
    const { fieldOutline } = this.props;
    const step = sectionNum;
    const section = fieldOutline.sections[step];
    if (section && this.isSignatureType(section.fieldIds[0])) {
      const fieldId = section.fieldIds[0];
      const parent = document.querySelector('#documentPreviewWrapper .self-serve-builder-wrapper');
      const child = document.getElementById(`preview-${fieldId}`);
      if (!parent || !child) return;
      const parentRect = parent.getBoundingClientRect();
      const childRect = child.getBoundingClientRect();

      const childIsBelowParentTop = (childRect.top >= parentRect.top);
      const childIsAboveParentBase = childRect.top <= (parentRect.top + parent.clientHeight);

      const isViewable = childIsBelowParentTop && childIsAboveParentBase;
      if (!isViewable) {
        // Scroll the parent container to the popup centered in viewport
        const parentMidPoint = parentRect.top + (parent.clientHeight / 2);
        parent.scrollTop = (childRect.top + parent.scrollTop) - parentMidPoint;
      }
    }
  }

  loadPreviousStaticBuilderSection() {
    this.props.loadPreviousSelfServeRecipientBuilderSection();
    this.scrollToSignature(this.props.step - 1);
  }

  async saveAndContinue() {
    const payload = serialize(document.getElementById('staticBuilderForm'));
    this.scrollToSignature(this.props.step + 1);
    return this.props.saveAndContinue(payload, this.props.shareKey);
  }

  saveAndSign(fieldId) {
    // Creating a payload object from the imageUrls redux object.
    // Grabbing last used image type which should be set as last created
    // signature, else defaulting to type.
    const signatureType = this.props.lastUsedImageType || 'type';
    const signature = this.props.imageUrls[signatureType];
    const payload = {};
    payload[fieldId] = {
      url: signature,
      type: signatureType,
    };

    this.props.saveSelfServeRecipientBuilderSectionContent(payload, this.props.shareKey);

    const trackingSignatureType = `Sign ${signatureType === 'type' ? 'Type' : 'Draw'} Signature`;
    if (this.props.isPreviewMode) {
      window._track(`${trackingSignatureType} | Preview Modal`, 'Template Builder');
    } else {
      this.props.createEventForDocumentShare(this.props.shareKey, `signature_add_${signatureType}`);
      window._track(`${trackingSignatureType} | Recipient Builder`, 'Recipient Builder');
    }
  }

  highlightSignature(sectionNum) {
    const section = this.props.fieldOutline.sections[sectionNum];
    if (section && this.isSignatureType(section.fieldIds[0])) {
      const fieldId = section.fieldIds[0];
      this.props.highlightField(fieldId);
    } else {
      this.props.highlightField('');
    }
  }

  renderEditorPreview() {
    const { documentType, fieldOutline, step } = this.props;
    const documentUrl = `${window.location.origin}/assets/${documentType}/document.pdf`;
    let popupGroups = {};
    let popupGroupsOnPage = {};
    let popupIdsOnPage = {};
    let popups = {};

    // Set popup fields if fieldOutline is defined and we are not in the signature declined state
    if (fieldOutline && this.props.accessLevel !== DocumentShareConstants.DECLINED_DONE_MODE) {
      popupGroups = fieldOutline.fieldGroups || {};
      popupGroupsOnPage = fieldOutline.popupGroupsOnPage || {};
      popupIdsOnPage = fieldOutline.fieldIdsOnPage || {};
      popups = fieldOutline.fields || {};
      if (!this.props.isLoading) {
        this.highlightSignature(step);
      }
    }

    return (
      <div className="span4 recipient-preview expanded" id="documentPreviewWrapper">
        {this.props.isShareFinished && (
          <div className="self-serve-recipient-buttons">
            <ButtonGroup
              documentLabel=""
              documentType={documentType}
              showDownloadTool
              showPrintButton
              showShareButton
              trackingContext="Recipient Builder"
            />
            <PrintButton
              showPrintButton
            />
            <div className="pull-right">
              <SignatureAuditButton
                onClick={SelfServeRecipientBuilder.trackAuditButtonClick}
                shareKey={this.props.shareKey}
              />
            </div>
          </div>
        )}
        <div className="document-preview-title static-builder">Document Preview</div>
        <SelfServeBuilderEditorWithTooltip>
          <SelfServeBuilderEditor
            documentType={documentType}
            documentUrl={documentUrl}
            isMenuOpen={this.props.isMenuOpen}
            isLoading={this.props.isLoading}
            isPreviewMode
            isPublic={this.props.isPublic}
            isRecipientBuilder
            isScalingEnabled
            isShared={this.props.isShared}
            pages={this.props.pages}
            popupGroups={popupGroups}
            popupGroupsOnPage={popupGroupsOnPage}
            popupIdsOnPage={popupIdsOnPage}
            popups={popups}
            publishedAt={this.props.publishedAt}
            setPagesFromFile={this.props.setPagesFromFile}
          />
        </SelfServeBuilderEditorWithTooltip>
      </div>
    );
  }

  renderExportButtons(sectionKeys = []) {
    const { documentType, isLoading } = this.props;
    return (
      <div id="exportButtons" className="builder-toolbar btn-toolbar static">
        <div
          id="navSideBarMenuButtonContainer"
          className="builder-side-bar-menu-button-container"
        >
          {!isLoading && (
            <NavSideBarMenuButtonWithRedux
              documentType={documentType}
            />
          )}
        </div>

        <div className="container-fluid builder-toolbar-inner-wrapper">
          <div id="sectionProgress" className="section-progress-wrapper">
            <StaticBuilderProgressBar
              onStep={this.props.step}
              numSteps={sectionKeys.length}
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const header = this.getHeader();
    const sectionKeys = this.getSectionKeys();
    const sectionsData = this.getFormattedSectionsData();
    const formattedFields = this.getFormattedFields();

    const staticBuilderFormattedFields = new StaticBuilderFormattedFields();
    staticBuilderFormattedFields.formattedFields = formattedFields;
    const {
      documentType,
      isEmptyState,
      isLoading,
      loadNextSelfServeRecipientBuilderSection: loadNext,
    } = this.props;
    const builderClass = this.props.isShareFinished ? 'finished-recipient-builder' : '';

    return (
      <React.Fragment>
        <div id="freeWriteUploadModal" />
        <div id="freeWriteDocumentReadyModal" />
        <div id="freeWriteExportDocumentModal" />
        <div id="DownloadOverlayContainer" />
        <RecipientBuilderIntroModal
          activeModal={this.state.activeModal}
          close={this.closeModal}
          documentType={this.props.documentType}
          isOpen={this.state.activeModal === ModalTypeEnum.SELF_SERVE_RECIPIENT_BUILDER_INTRO}
          shareKey={this.props.shareKey}
        />
        <RecipientBuilderFinishModal
          activeModal={this.state.activeModal}
          isOpen={this.state.activeModal === ModalTypeEnum.SELF_SERVE_RECIPIENT_BUILDER_FINISH}
          close={this.closeModal}
          finish={this.finish}
        />
        {!this.props.isShareFinished && (
          this.renderExportButtons(sectionKeys)
        )}
        <div id="builderContainer" className={`row-fluid builder recipient-builder ${builderClass}`}>
          {this.props.isShareFinished && (
            <div id="postShareSidebar" className="span5 post-share-sidebar">
              <SelfServeRecipientBuilderFinishedSidebar
                shareKey={this.props.shareKey}
                accessLevel={this.props.accessLevel}
              />
            </div>
          )}
          {this.props.isLoading && (
            <Spinner />
          )}
          {!this.props.isShareFinished && (
            <React.Fragment>
              <div id="builderNav" className="span3 navMargins builder-nav">
                <div id="well1" className="well well-no-padding">
                  <ul id="WizardNav" className="nav nav-list">
                    {!isLoading && (
                      <NavSideBarList
                        categories={sectionsData}
                        documentType={documentType}
                        onStep={this.props.step}
                        loadStaticBuilderSection={this.loadSection}
                      />
                    )}
                  </ul>
                </div>
              </div>
              <div id="staticBuilderContent" className="span5 static-builder-content expanded">
                <div id="SectionContent-load">
                  <div id="loadedContentArea">
                    {!isLoading && (
                      <StaticBuilderSection
                        disableConditionals
                        documentType={documentType}
                        formattedFields={staticBuilderFormattedFields}
                        header={header}
                        isLoading={this.props.isLoading}
                        isPreviewMode={this.props.isPreviewMode}
                        isSelfServe
                        loadPreviousStaticBuilderSection={this.loadPreviousStaticBuilderSection}
                        loadNextStaticBuilderSection={loadNext}
                        onStep={this.props.step}
                        finishSelfServe={this.handleFinish}
                        saveAndContinue={this.saveAndContinue}
                        saveAndSign={this.saveAndSign}
                        sectionKeys={sectionKeys}
                        shareKey={this.props.shareKey}
                      />
                    )}
                  </div>
                </div>
              </div>
              <DisableOverlay isEmptyState={isEmptyState} />
            </React.Fragment>
          )}
          {this.renderEditorPreview()}
        </div>
      </React.Fragment>
    );
  }
}


SelfServeRecipientBuilder.propTypes = {
  accessLevel: PropTypes.string,
  associateDocumentShare: PropTypes.func,
  createEventForDocumentShare: PropTypes.func.isRequired,
  documentName: PropTypes.string.isRequired,
  documentType: PropTypes.string.isRequired,
  fieldData: CustomPropTypes.hashmapOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.shape({
      type: PropTypes.string,
      url: PropTypes.string,
    }),
  ])),

  // Note: this is a different structure from `fieldOutline`
  // in static builder since `fieldOutline` contains the
  // entire builderOutline
  fieldOutline: PropTypes.shape({
    activeGroupId: PropTypes.string,
    activePopup: PropTypes.string,
    fieldGroups: CustomPropTypes.hashmapOf(
      PropTypes.shape({
        fieldIds: PropTypes.arrayOf(PropTypes.string),
        label: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    fields: CustomPropTypes.hashmapOf(
      PropTypes.shape({
        label: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        fieldIds: PropTypes.arrayOf(PropTypes.string),
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    fieldIdsOnPage: CustomPropTypes.hashmapOf(
      PropTypes.arrayOf(PropTypes.string),
    ).isRequired,
    popupGroupsOnPage: CustomPropTypes.hashmapOf(
      PropTypes.arrayOf(PropTypes.string),
    ).isRequired,
  }),
  finishAndSend: PropTypes.func.isRequired,
  getSectionDataForShare: PropTypes.func.isRequired,
  highlightField: PropTypes.func.isRequired,
  imageUrls: PropTypes.shape({
    draw: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  isEmptyState: PropTypes.bool,
  isMenuOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  isPreviewMode: PropTypes.bool,
  isRecipientPreviewModal: PropTypes.bool,
  isShareFinished: PropTypes.bool.isRequired,
  isShared: PropTypes.bool.isRequired,
  lastUsedImageType: PropTypes.string,
  loadNextSelfServeRecipientBuilderSection: PropTypes.func.isRequired,
  loadPreviousSelfServeRecipientBuilderSection: PropTypes.func.isRequired,
  loadSelfServeRecipientBuilderSection: PropTypes.func.isRequired,
  pages: PropTypes.arrayOf(PropTypes.shape()),
  publishedAt: PropTypes.bool,
  saveAndContinue: PropTypes.func.isRequired,
  saveSelfServeRecipientBuilderSectionContent: PropTypes.func.isRequired,
  setPagesFromFile: PropTypes.func.isRequired,
  setIsScalingEnabled: PropTypes.func.isRequired,
  shareKey: PropTypes.string.isRequired,
  step: PropTypes.number,
};

SelfServeRecipientBuilder.defaultProps = {
  accessLevel: '',
  associateDocumentShare: () => null,
  fieldData: {},
  fieldOutline: null,
  isEmptyState: false,
  isPreviewMode: false,
  lastUsedImageType: '',
  isRecipientPreviewModal: false,
  pages: [],
  publishedAt: null,
  step: 0,
};

const mapStateToProps = ({
  StaticBuilderReducer,
  SelfServeBuilderReducer,
  SignatureHandler,
}) => ({
  accessLevel: SelfServeBuilderReducer.accessLevel,
  documentName: SelfServeBuilderReducer.documentName,
  documentType: StaticBuilderReducer.documentType,
  fieldOutline: StaticBuilderReducer.fieldOutline,
  fieldData: StaticBuilderReducer.fieldData,
  imageUrls: SignatureHandler.imageUrls,
  isLoading: StaticBuilderReducer.isLoading,
  isMenuOpen: StaticBuilderReducer.isMenuOpen,
  isPublic: SelfServeBuilderReducer.isPublic,
  isShareFinished: SelfServeBuilderReducer.isShareFinished,
  isShared: SelfServeBuilderReducer.isShared,
  lastUsedImageType: SignatureHandler.lastUsedImageType,
  publishedAt: SelfServeBuilderReducer.publishedAt,
  pages: SelfServeBuilderReducer.pages,
  step: StaticBuilderReducer.step,
});

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    associateDocumentShare,
    createEventForDocumentShare,
    finishAndSend,
    getSectionDataForShare,
    highlightField,
    saveSelfServeRecipientBuilderSectionContent,
    loadNextSelfServeRecipientBuilderSection,
    loadPreviousSelfServeRecipientBuilderSection,
    loadSelfServeRecipientBuilderSection,
    saveAndContinue,
    setIsScalingEnabled,
    setPagesFromFile,
  }, dispatch)
);

export const SelfServeRecipientBuilderWithRedux = ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelfServeRecipientBuilder);

export default withInit(SelfServeRecipientBuilderWithRedux, true);



// WEBPACK FOOTER //
// ./src-react/SelfServeRecipientBuilder/index.jsx